import {Injectable} from "@angular/core";
import {DatePipe} from "@angular/common";
import {ColDef} from "ag-grid-community";
import {FormatCurrencyPipe} from "../../pipes/format-currency.pipe";
import {PlayerNoteReasonType} from "../../enum/PlayerNoteReasonType";
import {PlayerSuspensionReasonTypes} from "../../enum/PlayerSuspensionReasonTypes";
import {FormatBoolean} from "../cell-renderers/show-played/format-boolean.component";
import {GameTypeEnum} from "../../enum/GameTypeEnum";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";
import {
  PlayerTransactionOptionsComponent
} from "../cell-renderers/player-transaction-options/player-transaction-options.component";
import {
  GoldRushAutoplaySettingsComponent
} from "../cell-renderers/gold-rush-autoplay-settings/gold-rush-autoplay-settings.component";
import {
  FiftyFiftyAutoplaySettingsComponent
} from "../cell-renderers/fifty-fifty-autoplay-settings/fifty-fifty-autoplay-settings.component";

@Injectable({
  providedIn: 'root'
})
export class PlayerInfoTableDefs {

  public playerPledgesColDefs: ColDef[] = [
    {
      headerName: 'Game Name',
      field: 'GameName',
      resizable: true,
      sortable: true,
    },
    {
      headerName: 'Charity Name',
      field: 'CharityName',
      resizable: true,
      sortable: true
    },
    {
      headerName: 'Created On',
      resizable: true,
      sortable: true,
      width: 150,
      field: 'CreatedOn',
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y');
        return convertedDate ? convertedDate : '';
      },
    },
    {
      headerName: 'Active',
      cellRenderer: FormatBoolean,
      field: 'Active',
    },
    {
      headerName: 'DeactivatedOn',
      resizable: true,
      sortable: true,
      field: 'DeactivatedOn',
      valueFormatter: params => {
        if (params.value) {
          const convertedDate = this.datePipe.transform(params.value, 'MMM d, y');
          return convertedDate ? convertedDate : '';
        }
        return '';
      },
    },
  ];

  public transactionColumnDefs: ColDef[] = [
    {
      headerName: 'Game Name',
      field: 'GameName',
      resizable: true,
      sortable: true,
    },
    {
      headerName: 'Charge Id',
      width: 250,
      sortable: true,
      field: 'StripeChargeId',
      resizable: true
    },
    {
      headerName: 'Card Fingerprint',
      width: 200,
      sortable: true,
      field: 'CardFingerprint',
      resizable: true,
      valueFormatter: params => {
        return params.value ? params.value : 'N/A';
      }
    },
    {
      headerName: 'Total ($)',
      resizable: true,
      width: 95,
      field: 'Total',
      sortable: true,
      valueFormatter: params => {
        const convertedDate = this.formatCurrencyPipe.transform(params.value);
        return convertedDate ? convertedDate : '';
      }
    },
    {
      headerName: 'Transaction Date',
      resizable: true,
      sortable: true,
      sort: 'desc',
      width: 150,
      field: 'CreatedOn',
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y HH:mm');
        return convertedDate ? convertedDate : '';
      }
    },
    {
      headerName: 'Ticket Numbers',
      resizable: true,
      sortable: true,
      width: 120,
      field: 'TicketNumbers',
      valueFormatter: params => {
        return params.value.toString();
      }
    },
    {
      headerName: 'Draw Date',
      resizable: true,
      sortable: true,
      width: 250,
      field: 'GameInstanceEndedOn',
      valueFormatter: params => {
        const convertedEndDate = this.datePipe.transform(params.data.GameInstanceEndedOn, 'MMM d, y');
        if (convertedEndDate) {
          return convertedEndDate;
        }
        return '';
      }
    },
    {
      headerName: '',
      width: 150, minWidth: 50, maxWidth: 150,
      cellRenderer: PlayerTransactionOptionsComponent,
    }
  ];

  public refundColumnDefs: ColDef[] = [
    {
      headerName: 'Charge Id',
      width: 200,
      sortable: true,
      field: 'StripeChargeId',
      resizable: true
    },
    {
      headerName: 'Refund Id',
      width: 200,
      sortable: true,
      field: 'StripeRefundId',
      resizable: true
    },
    {
      headerName: 'Game Instance',
      resizable: true,
      sortable: true,
      width: 200,
      field: 'GameInstanceId'
    },
    {
      headerName: 'Date of Refund',
      resizable: true,
      sortable: true,
      sort: 'desc',
      width: 250,
      field: 'RefundCreatedOn',
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y HH:mm');
        return convertedDate ? convertedDate : '';
      }
    },
    {
      headerName: 'Total ($)',
      resizable: true,
      width: 95,
      field: 'RefundAmount',
      sortable: true,
      valueFormatter: params => {
        const convertedDate = this.formatCurrencyPipe.transform(params.value);
        return convertedDate ? convertedDate : '';
      }
    }
  ];

  public failedAutoplayStripsColumnDefs: ColDef[] = [
    {
      headerName: 'Strip Id',
      field: 'Id',
      resizable: true,
      sortable: true
    },
    {
      headerName: 'Payment Tier',
      field: 'PaymentTierString',
      resizable: true,
      sortable: true
    },
    {
      headerName: 'Numbers',
      resizable: true,
      sortable: true,
      field: 'TicketNumbers'
    }
  ];

  public getColumnDefsForGameType(game: IGameQueryResult): ColDef[] {
    return game.Type === GameTypeEnum.GoldRush
      ? this.goldRushStripsColumnDefs
      : this.fiftyFiftyStripsColumnDefs;
  }

  public goldRushStripsColumnDefs: ColDef[] = [
    {
      headerName: 'Strip Id',
      field: 'Id',
      resizable: true,
      sortable: true,
    },
    {
      headerName: 'Ticket Numbers',
      field: 'TicketNumbers',
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        return params.data.TicketNumbers.TicketNumber;
      }
    },
    {
      headerName: "Created On",
      field: 'CreatedOn',
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y HH:mm');
        if (convertedDate) {
          return convertedDate;
        }
        return '';
      }
    },
    {
      headerName: 'Autoplay Enabled',
      field: 'Autoplay',
      resizable: true,
      sortable: true
    },
    {
      headerName: 'Active',
      field: 'Active',
      resizable: true,
      sortable: true
    },
    {
      headerName: '',
      width: 150, minWidth: 50, maxWidth: 150,
      cellRenderer: GoldRushAutoplaySettingsComponent,
    }
  ];

  public fiftyFiftyStripsColumnDefs: ColDef[] = [
    {
      headerName: 'Strip ID',
      field: 'Id',
      resizable: true,
      sortable: true,
    },
    {
      headerName: 'Ticket Numbers',
      field: 'TicketNumbers',
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        // TicketNumbers is already an array of strings for 50/50
        return params.value ? params.value.join(', ') : '';
      }
    },
    {
      headerName: 'Purchased On',
      field: 'PurchasedOn',
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y HH:mm');
        return convertedDate ? convertedDate : '';
      }
    },
    {
      headerName: 'Autoplayed',
      field: 'Autoplay',
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        return params.value ? 'Yes' : 'No';
      }
    }
  ];

  public playedNumberGroupsColumnDefs: ColDef[] = [
    {
      headerName: 'Strip Id',
      field: 'Id',
      resizable: true,
      sortable: true,
    },
    {
      headerName: 'Numbers',
      resizable: true,
      sortable: true,
      field: 'TicketNumbers'
    },
    {
      headerName: 'Played',
      resizable: true,
      sortable: true,
      field: 'Played',
      valueFormatter: params => {
        return params.value ? 'Yes' : 'No'
      }
    },
    {
      headerName: 'Strip Autoplay Enabled',
      resizable: true,
      sortable: true,
      field: 'Autoplay',
      valueFormatter: params => {
        return params.value ? 'Yes' : 'No'
      }
    },
    {
      headerName: 'Played Via Autoplay',
      resizable: true,
      sortable: true,
      field: 'PurchasedViaAutoplay',
      valueFormatter: params => {
        return params.value ? 'Yes' : 'No'
      }
    }
  ];

  public deactivatedStripsColumnDefs: ColDef[] = [
    {
      headerName: 'Strip Id',
      field: 'Id',
      resizable: true,
      sortable: true,
    },
    {
      headerName: 'Numbers',
      resizable: true,
      sortable: true,
      field: 'TicketNumbers'
    },
    {
      headerName: 'DeactivatedOn',
      resizable: true,
      sortable: true,
      field: 'DeactivatedOn',
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y HH:mm');
        return convertedDate ? convertedDate : '';
      }
    }
  ];

  public playerNotesColumnDefs: ColDef[] = [
    {
      headerName: "Message",
      field: "Message",
      resizable: true,
      sortable: true
    },
    {
      headerName: "Created On",
      field: "CreatedOn",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y HH:mm');
        return convertedDate ? convertedDate : '';
      }
    },
    {
      headerName: "Reason Type",
      field: "ReasonType",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        return PlayerNoteReasonType[params.value];
      }
    },
    {
      headerName: "Last Updated By",
      field: "LastUpdatedBy",
      resizable: true,
      sortable: true
    },
    {
      headerName: "Causable Admin Note",
      field: "IsCausableAdminNote",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        return params.value ? "Yes" : "No"
      }
    }
  ];

  public playerSuspensionColumnDefs: ColDef[] = [
    {
      headerName: 'Game Name',
      field: 'GameName',
      resizable: true,
      sortable: true,
    },
    {
      headerName: "Player Suspension Id",
      field: "Id",
      resizable: true,
      sortable: true
    },
    {
      headerName: "Player Id",
      field: "PlayerId",
      resizable: true,
      sortable: true
    },
    {
      headerName: "Suspension Reason Type",
      field: "PlayerSuspensionReasonType",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        return PlayerSuspensionReasonTypes[params.value]
      }
    },
    {
      headerName: "Created On",
      field: "CreatedOn",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y HH:mm');
        return convertedDate ? convertedDate : '';
      }
    },
    {
      headerName: "Resolved",
      field: "Resolved",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        return params.value ? "Yes" : "No"
      }
    },
    {
      headerName: "Resolved On",
      field: "ResolvedOn",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        //DateTime.Min check
        if (params.value == "0001-01-01T00:00:00Z") {
          return "";
        }
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y HH:mm');
        return convertedDate!;
      }
    },
    {
      headerName: "Causable Suspension",
      field: "CausableSuspension",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        return params.value ? "Yes" : "No"
      }
    }
  ]

  public sendGridMessageDataColDefs: ColDef[] = [
    {
      headerName: "From Email",
      field: "from_email",
      resizable: true,
      sortable: true
    },
    {
      headerName: "Message Id",
      field: "msg_id",
      resizable: true,
      sortable: true,
    },
    {
      headerName: "Subject",
      field: "Subject",
      resizable: true,
      sortable: true,
    },
    {
      headerName: "To Email",
      field: "to_email",
      resizable: true,
      sortable: true,
    },
    {
      headerName: "Status",
      field: "Status",
      resizable: true,
      sortable: true,
    },
    {
      headerName: "Open Count",
      field: "opens_count",
      resizable: true,
      sortable: true,
    },
    {
      headerName: "Click Count",
      field: "clicks_count",
      resizable: true,
      sortable: true,
    },
    {
      headerName: "Last Event Time",
      field: "last_event_time",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y HH:mm');
        return convertedDate!;
      }
    }
  ];

  public playerEmailHistoryColDefs: ColDef[] = [
    {
      headerName: "Email",
      field: "Email",
      resizable: true,
      sortable: true
    },
    {
      headerName: "Created On",
      field: "CreatedOn",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y HH:mm');
        return convertedDate!;
      }
    },
    {
      headerName: "Active",
      field: "Active",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        return params.value ? "Yes" : "No"
      }
    },
    {
      headerName: "Deactivated On",
      field: "DeactivatedOn",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y HH:mm');
        return convertedDate!;
      }
    }
  ];

  public playerGameStatsColDefs: ColDef[] = [
    {
      headerName: "Game Id",
      field: "GameId",
      resizable: true,
      sortable: true,
    },
    {
      headerName: "Game Name",
      field: "GameName",
      resizable: true,
      sortable: true
    },
    {
      headerName: "Times Won",
      field: "TimesWon",
      resizable: true,
      sortable: true
    },
    {
      headerName: "Times Drawn But Number Was Not Played",
      field: "TimesNumberWasDrawnButNotPlayed",
      resizable: true,
      sortable: true
    },
    {
      headerName: "Total Spent",
      field: "TotalSpent",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        const converted_amount = this.formatCurrencyPipe.transform(params.value * 100);
        return converted_amount ?? "$0.00";
      }
    }
  ];

  public playerWinnerColDefs: ColDef[] = [
    {
      headerName: "Game Name",
      field: "GameName",
      resizable: true,
      sortable: true,
    },
    {
      headerName: "Ticket Number",
      field: "TicketNumber",
      resizable: true,
      sortable: true,
    },
    {
      headerName: "Draw Time",
      field: "DrawTime",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, "MMM dd, yyyy")
        return convertedDate ?? "";
      }
    },
    {
      headerName: "Draw Amount",
      field: "PlayerWinnings",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        const convertedCurrency = this.formatCurrencyPipe.transform(params.value);
        return convertedCurrency ?? "$0.00";
      }
    },
    {
      headerName: "Payout Status",
      field: "PayoutStatus",
      resizable: true,
      sortable: true,
    }
  ];

  public autoplayGroupsColumnDefs: ColDef[] = [
    {
      headerName: 'Autoplay Group ID',
      field: 'Id',
      resizable: true,
      sortable: true,
    },
    {
      headerName: 'Payment Tier ID',
      field: 'PaymentTierId',
      resizable: true,
      sortable: true,
    },
    {
      headerName: 'Created On',
      field: 'CreatedOn',
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y HH:mm');
        return convertedDate ? convertedDate : '';
      }
    },
    {
      headerName: 'Active',
      field: 'Active',
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        return params.value ? 'Yes' : 'No';
      }
    },
    {
      headerName: '',
      width: 150,
      minWidth: 50,
      maxWidth: 150,
      cellRenderer: FiftyFiftyAutoplaySettingsComponent,
    }
  ];

  constructor(private datePipe: DatePipe,
              private formatCurrencyPipe: FormatCurrencyPipe) {
  }

}
